import { useRef, useEffect, useState, useMemo } from "react";
import { CheckCircle, Lock, BookOpen, Play, Star, ZoomIn, ZoomOut } from "lucide-react";
import MasteryBadge from "@/components/MasteryBadge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";

interface SkillNode {
  id: string;
  title: string;
  description: string | null;
  level_index: number;
  position_index: number;
  is_key_node: boolean;
  chapter_id?: string | null;
  user_status: "locked" | "available" | "in_progress" | "completed";
}

interface Prerequisite {
  id: string;
  skill_node_id: string;
  prereq_node_id: string;
}

export interface NodeMastery {
  mastery_score: number;
  confidence_tier: string;
}

interface CourseTreeViewProps {
  nodes: SkillNode[];
  prerequisites: Prerequisite[];
  selectedNodeId: string | null;
  onNodeSelect: (node: SkillNode) => void;
  isDesignerPreview?: boolean;
  /** AI Coach: per-node mastery for MasteryBadge (node_id -> mastery) */
  nodeMastery?: Record<string, NodeMastery>;
}

// ─── Graph-based level computation ───────────────────────────────────────────
function computeGraphLevels(
  nodes: { id: string }[],
  prerequisites: { skill_node_id: string; prereq_node_id: string }[],
): Map<string, number> {
  const prereqsOf = new Map<string, string[]>();
  nodes.forEach(n => prereqsOf.set(n.id, []));
  prerequisites.forEach(p => prereqsOf.get(p.skill_node_id)?.push(p.prereq_node_id));

  const levels = new Map<string, number>();
  const visiting = new Set<string>();

  const visit = (id: string): number => {
    if (levels.has(id)) return levels.get(id)!;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const prereqs = prereqsOf.get(id) ?? [];
    const level = prereqs.length === 0 ? 0 : Math.max(...prereqs.map(visit)) + 1;
    visiting.delete(id);
    levels.set(id, level);
    return level;
  };

  nodes.forEach(n => visit(n.id));
  return levels;
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const LEVEL_GAP   = 190;
const VERT_GAP    = 110;
const H_PAD       = 96;
const VIEW_H      = 320;
const NODE_W      = 76;
const NODE_W_SEL  = 96;
const ZOOM_MIN    = 0.25;
const ZOOM_MAX    = 3.0;
const ZOOM_STEP   = 1.15;

// ─── Colours ──────────────────────────────────────────────────────────────────
const C = {
  selectedBg:      "#EAE6DC",
  selectedBorder:  "hsl(65,65%,75%)",
  completedBg:     "#2F6147",
  completedBorder: "rgba(255,255,255,0.1)",
  inProgressBg:    "rgba(47,97,71,0.55)",
  inProgressBorder:"rgba(212,224,138,0.35)",
  availableBg:     "rgba(234,230,220,0.10)",
  availableBorder: "rgba(234,230,220,0.22)",
  lockedBg:        "rgba(234,230,220,0.04)",
  lockedBorder:    "rgba(234,230,220,0.07)",
  textOnDark:      "rgba(234,230,220,0.75)",
  textOnLight:     "#1C3829",
  lime:            "hsl(65,65%,75%)",
} as const;

function nodeStyle(
  node: SkillNode,
  isSelected: boolean,
  w: number,
  cx: number,
  cy: number,
): React.CSSProperties {
  let bg: string, border: string, borderWidth: string;
  if (isSelected && node.user_status !== "locked") {
    bg = C.selectedBg; border = C.selectedBorder; borderWidth = "2.5px";
  } else {
    borderWidth = "1.5px";
    switch (node.user_status) {
      case "completed":   bg = C.completedBg;  border = C.completedBorder;  break;
      case "in_progress": bg = C.inProgressBg; border = C.inProgressBorder; break;
      case "available":   bg = C.availableBg;  border = C.availableBorder;  break;
      default:            bg = C.lockedBg;     border = C.lockedBorder;
    }
  }
  return {
    position: "absolute",
    left: cx - w / 2, top: cy - w / 2,
    width: w, height: w,
    borderRadius: 14,
    border: `${borderWidth} solid ${border}`,
    background: bg,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    gap: 4, padding: 6,
    cursor: node.user_status === "locked" ? "not-allowed" : "pointer",
    zIndex: isSelected ? 4 : 2,
    opacity: node.user_status === "locked" ? 0.42 : 1,
    userSelect: "none",
  };
}

function edgeColor(from: SkillNode, to: SkillNode): string {
  if (from.user_status === "completed" &&
      (to.user_status === "completed" || to.user_status === "in_progress")) return "#3D7A58";
  if (from.user_status === "completed" && to.user_status === "available")    return "rgba(74,154,108,0.55)";
  if (from.user_status === "in_progress")                                     return "rgba(74,154,108,0.4)";
  return "rgba(255,255,255,0.07)";
}

function NodeIcon({ node, isSelected }: { node: SkillNode; isSelected: boolean }) {
  const color = isSelected ? C.textOnLight : C.lime;
  const size  = isSelected ? 24 : 18;
  const props = { style: { color, width: size, height: size, display: "block" as const } };
  if (node.user_status === "completed")   return <CheckCircle {...props} />;
  if (node.user_status === "locked")      return <Lock {...props} />;
  if (node.user_status === "in_progress") return <BookOpen {...props} />;
  return <Play {...props} />;
}

// ─── Transform state ──────────────────────────────────────────────────────────
// (panX, panY) = world coordinate of the viewport top-left corner
// CSS: transform-origin 0 0; transform: scale(scale) translate(-panX, -panY)
interface Transform { x: number; y: number; scale: number }

export default function CourseTreeView({
  nodes,
  prerequisites,
  selectedNodeId,
  onNodeSelect,
  isDesignerPreview = false,
  nodeMastery = {},
}: CourseTreeViewProps) {
  const { toast } = useToast();
  const viewportRef = useRef<HTMLDivElement>(null);

  const [tf, setTf]           = useState<Transform>({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  // Smoothly animated only for auto-pan; disabled during user interaction.
  const [animated, setAnimated] = useState(true);

  // Drag tracking
  const dragRef  = useRef<{ sx: number; sy: number; stx: number; sty: number } | null>(null);
  // Set to true if the pointer moved enough to count as a drag (suppresses click).
  const movedRef = useRef(false);

  // ─── Layout ────────────────────────────────────────────────────────────────
  const { positions, innerWidth, innerHeight } = useMemo(() => {
    const uniqueLevelCount = new Set(nodes.map(n => n.level_index)).size;
    const graphLevels = uniqueLevelCount <= 1
      ? computeGraphLevels(nodes, prerequisites)
      : null;
    const nodeLevel = (n: SkillNode) => graphLevels ? (graphLevels.get(n.id) ?? 0) : n.level_index;

    const byLevel: Record<number, SkillNode[]> = {};
    nodes.forEach(n => { (byLevel[nodeLevel(n)] ??= []).push(n); });
    const levels = Object.keys(byLevel).map(Number).sort((a, b) => a - b);
    const maxCount = Math.max(...levels.map(l => byLevel[l].length), 1);
    const innerH = Math.max(VIEW_H, maxCount * VERT_GAP + 80);
    const innerW = H_PAD + levels.length * LEVEL_GAP + H_PAD;

    const positions = new Map<string, { cx: number; cy: number }>();
    levels.forEach((level, idx) => {
      const lNodes = [...byLevel[level]].sort((a, b) => a.position_index - b.position_index);
      const count = lNodes.length;
      const startY = innerH / 2 - ((count - 1) * VERT_GAP) / 2;
      lNodes.forEach((node, i) => {
        positions.set(node.id, { cx: H_PAD + idx * LEVEL_GAP, cy: startY + i * VERT_GAP });
      });
    });
    return { positions, innerWidth: innerW, innerHeight: innerH };
  }, [nodes, prerequisites]);

  // ─── Auto-pan to selected node ────────────────────────────────────────────
  const doPan = (nodeId: string | null) => {
    if (!nodeId || !viewportRef.current) return;
    const pos = positions.get(nodeId);
    if (!pos) return;
    const vpW = viewportRef.current.offsetWidth;
    const vpH = viewportRef.current.offsetHeight;
    setAnimated(true);
    setTf(prev => ({
      ...prev,
      x: pos.cx - (vpW * 0.40) / prev.scale,
      y: pos.cy - (vpH * 0.50) / prev.scale,
    }));
  };

  useEffect(() => { doPan(selectedNodeId); }, [selectedNodeId, positions]);

  useEffect(() => {
    const obs = new ResizeObserver(() => doPan(selectedNodeId));
    if (viewportRef.current) obs.observe(viewportRef.current);
    return () => obs.disconnect();
  }, [selectedNodeId, positions]);

  // ─── Scroll-to-zoom (non-passive wheel listener) ──────────────────────────
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const vx = e.clientX - rect.left;
      const vy = e.clientY - rect.top;
      setAnimated(false);
      setTf(t => {
        const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
        const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.scale * factor));
        return {
          scale: newScale,
          x: t.x + vx * (1 / t.scale - 1 / newScale),
          y: t.y + vy * (1 / t.scale - 1 / newScale),
        };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // ─── Drag handlers ────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    movedRef.current = false;
    dragRef.current = { sx: e.clientX, sy: e.clientY, stx: tf.x, sty: tf.y };
    setIsDragging(true);
    setAnimated(false);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
    const { stx, sty } = dragRef.current;
    setTf(t => ({ ...t, x: stx - dx / t.scale, y: sty - dy / t.scale }));
  };

  const stopDrag = () => {
    setIsDragging(false);
    dragRef.current = null;
  };

  // Touch: single-finger drag
  const touchRef = useRef<{ id: number; sx: number; sy: number; stx: number; sty: number } | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const t0 = e.touches[0];
    movedRef.current = false;
    touchRef.current = { id: t0.identifier, sx: t0.clientX, sy: t0.clientY, stx: tf.x, sty: tf.y };
    setAnimated(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchRef.current || e.touches.length !== 1) return;
    const t0 = e.touches[0];
    if (t0.identifier !== touchRef.current.id) return;
    const dx = t0.clientX - touchRef.current.sx;
    const dy = t0.clientY - touchRef.current.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
    const { stx, sty } = touchRef.current;
    setTf(t => ({ ...t, x: stx - dx / t.scale, y: sty - dy / t.scale }));
  };

  const onTouchEnd = () => { touchRef.current = null; };

  // ─── Node click (suppressed when drag occurred) ───────────────────────────
  const handleNodeClick = (node: SkillNode) => {
    if (movedRef.current) { movedRef.current = false; return; }
    if (node.user_status === "locked" && !isDesignerPreview) {
      toast({ title: "Node locked", description: "Complete prerequisite nodes to unlock this one." });
      return;
    }
    onNodeSelect(node);
  };

  // ─── Zoom buttons ─────────────────────────────────────────────────────────
  const zoomBy = (factor: number) => {
    if (!viewportRef.current) return;
    const vpW = viewportRef.current.offsetWidth;
    const vpH = viewportRef.current.offsetHeight;
    const vx = vpW / 2, vy = vpH / 2; // zoom centered on viewport
    setAnimated(true);
    setTf(t => {
      const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.scale * factor));
      return {
        scale: newScale,
        x: t.x + vx * (1 / t.scale - 1 / newScale),
        y: t.y + vy * (1 / t.scale - 1 / newScale),
      };
    });
  };

  // ─── SVG edges ────────────────────────────────────────────────────────────
  const edges = useMemo(() =>
    prerequisites.flatMap(p => {
      const from = nodes.find(n => n.id === p.prereq_node_id);
      const to   = nodes.find(n => n.id === p.skill_node_id);
      if (!from || !to) return [];
      const fp = positions.get(from.id);
      const tp = positions.get(to.id);
      if (!fp || !tp) return [];
      const x1 = fp.cx + NODE_W / 2, y1 = fp.cy;
      const x2 = tp.cx - NODE_W / 2, y2 = tp.cy;
      const mx = (x1 + x2) / 2;
      return [{
        d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`,
        stroke: edgeColor(from, to),
        key: p.id,
      }];
    }),
  [prerequisites, nodes, positions]);

  // ─── Render ───────────────────────────────────────────────────────────────
  const btnStyle: React.CSSProperties = {
    width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(234,230,220,0.18)",
    background: "rgba(28,56,41,0.85)", color: "rgba(234,230,220,0.8)",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", backdropFilter: "blur(4px)",
  };

  return (
    <TooltipProvider>
      <div
        ref={viewportRef}
        style={{
          height: VIEW_H,
          overflow: "hidden",
          position: "relative",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Gradient fades — fixed to viewport */}
        <div style={{ position:"absolute",top:0,bottom:0,left:0,width:100,zIndex:3,pointerEvents:"none",
          background:"linear-gradient(to right,#1C3829 0%,transparent 100%)" }} />
        <div style={{ position:"absolute",top:0,bottom:0,right:0,width:180,zIndex:3,pointerEvents:"none",
          background:"linear-gradient(to left,#1C3829 5%,transparent 100%)" }} />

        {/* Zoom controls */}
        <div style={{ position:"absolute", bottom:10, right:10, zIndex:10, display:"flex", flexDirection:"column", gap:4, pointerEvents:"all" }}>
          <button style={btnStyle} onClick={e => { e.stopPropagation(); zoomBy(ZOOM_STEP); }} title="Zoom in">
            <ZoomIn style={{ width:14, height:14 }} />
          </button>
          <button style={btnStyle} onClick={e => { e.stopPropagation(); zoomBy(1 / ZOOM_STEP); }} title="Zoom out">
            <ZoomOut style={{ width:14, height:14 }} />
          </button>
        </div>

        {/* Pannable + zoomable inner container */}
        <div
          style={{
            position: "absolute", top: 0, left: 0,
            width: innerWidth, height: innerHeight,
            transformOrigin: "0 0",
            transform: `scale(${tf.scale}) translate(${-tf.x}px, ${-tf.y}px)`,
            transition: animated && !isDragging
              ? "transform 0.45s cubic-bezier(0.4,0,0.2,1)"
              : "none",
            // During drag, pass pointer events through to the viewport for smooth panning
            pointerEvents: isDragging ? "none" : "auto",
          }}
        >
          {/* SVG connection lines */}
          <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none" }}>
            {edges.map(e => (
              <path key={e.key} d={e.d} fill="none" stroke={e.stroke} strokeWidth={2} strokeLinecap="round" />
            ))}
          </svg>

          {/* Nodes */}
          {nodes.map(node => {
            const pos = positions.get(node.id);
            if (!pos) return null;
            const isSelected = node.id === selectedNodeId;
            const w = isSelected && node.user_status !== "locked" ? NODE_W_SEL : NODE_W;
            const textColor = isSelected && node.user_status !== "locked" ? C.textOnLight : C.textOnDark;

            return (
              <Tooltip key={node.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleNodeClick(node)}
                    disabled={node.user_status === "locked" && !isDesignerPreview}
                    className={`tree-node${isSelected && node.user_status !== "locked" ? " tree-node-selected" : ""}`}
                    style={nodeStyle(node, isSelected, w, pos.cx, pos.cy)}
                    aria-label={node.title}
                    onMouseDown={e => e.stopPropagation()} // prevent viewport drag starting inside node
                  >
                    <NodeIcon node={node} isSelected={isSelected} />
                    <span style={{
                      fontSize: isSelected ? 10.5 : 9.5,
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.25,
                      color: textColor,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      maxWidth: "100%",
                      wordBreak: "break-word",
                    }}>
                      {node.title}
                    </span>
                    {node.is_key_node && (
                      <div style={{
                        position:"absolute", top:-7, right:-7,
                        width:18, height:18, borderRadius:9,
                        background:"hsl(65,65%,75%)", border:"2px solid #1C3829",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <Star style={{ width:9,height:9,color:"#1C3829",fill:"#1C3829" }} />
                      </div>
                    )}
                    {nodeMastery[node.id] && (
                      <div style={{ position:"absolute", bottom:-4, left:"50%", transform:"translateX(-50%)" }}>
                        <MasteryBadge
                          confidenceTier={nodeMastery[node.id].confidence_tier as "none"|"low"|"medium"|"high"|"mastered"}
                          masteryScore={nodeMastery[node.id].mastery_score}
                          size="sm"
                        />
                      </div>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="font-semibold">{node.title}</p>
                  {node.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{node.description}</p>
                  )}
                  {node.user_status === "locked" && !isDesignerPreview && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Complete prerequisites first
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
